import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useGoogleLogin } from '@react-oauth/google'
import { CookiePreferencesLink } from '@/components/Consent/CookiePreferencesLink'
import { observer } from 'mobx-react-lite'
import { Loader2, Mail, ArrowLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { gql } from 'graphql-request'
import axios from 'axios'

import { Button } from '@/components/Common/Button'
import { Input } from '@/components/ui/input'
import { useStores } from '@/components/StoreProvider'
import Layout from '@/components/Layout/Layout'
import MicrosoftLoginButton from '@/components/Auth/MicrosoftLoginButton'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { normalizeEmail } from '@/lib/utils'
import SignInPage from './SignInPage'
import { useSignInBrand, type JoinLinkContext } from './useSignInBrand'

interface RedeemOutcome {
  moduleId: number
  moduleName: string
  granted: boolean
  status: string
  message: string | null
}

// Sent by the server when a member of a B2B organisation opens a B2C link.
const JOIN_LINK_PERSONAL_ONLY = 'JOIN_LINK_PERSONAL_ONLY'

const REDEEM_JOIN_TOKEN = gql`
  mutation redeemJoinToken($input: RedeemJoinTokenInput!) {
    redeemJoinToken(input: $input) {
      success
      message
      grantedCount
      outcomes {
        moduleId
        moduleName
        granted
        status
        message
      }
    }
  }
`

// Google icon component with official colors
const GoogleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false" className="shrink-0">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
)

// The mock's controls: white SSO buttons with a thin border, a brand green send button.
const ssoButtonClass =
  'h-12 w-full justify-center gap-2.5 rounded-xl border-[1.5px] border-[#E8EDE9] bg-white text-sm font-semibold text-[#0F4C3A] shadow-none transition-all hover:border-[#D6EBDE] hover:bg-[#F2F8F4] active:scale-[0.99]'
const primaryButtonClass =
  'h-12 w-full justify-center gap-2 rounded-xl border-0 bg-[#0F4C3A] text-sm font-bold tracking-[0.3px] text-white shadow-none transition-all hover:bg-[#073529] active:scale-[0.99] disabled:opacity-50'
const fieldClass =
  'h-12 rounded-xl border-[1.5px] border-[#E8EDE9] bg-white px-4 text-sm text-[#0F4C3A] shadow-none placeholder:text-[#95A39A] focus-visible:border-[#A4D447] focus-visible:ring-[3px] focus-visible:ring-[#C5F26A]/25'

const Auth = observer(() => {
  const navigate = useNavigate()
  const { userStore, settingsStore } = useStores()
  const { t } = useTranslation()
  const from = '/'

  // Read once, synchronously, before anything can navigate away from this URL.
  const [joinToken, setJoinToken] = useState<string | null>(() => new URLSearchParams(window.location.search).get('token'))
  const [joinContext, setJoinContext] = useState<JoinLinkContext | null>(null)
  const [redeeming, setRedeeming] = useState(false)
  const [redeemOutcomes, setRedeemOutcomes] = useState<RedeemOutcome[] | null>(null)
  const [redeemError, setRedeemError] = useState<string | null>(null)

  // The brand is pinned to the token the page was opened with: `joinToken` is cleared
  // after redemption, and the page must not snap back to Georgia at that moment.
  const [brandToken] = useState(joinToken)
  const brandState = useSignInBrand(brandToken, joinContext)

  // Strip the token from the address bar. Query strings persist in browser history
  // and server access logs, and this one grants organisation membership.
  useEffect(() => {
    if (!joinToken) return
    const url = new URL(window.location.href)
    url.searchParams.delete('token')
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
  }, [joinToken])

  // Public endpoint — deliberately reachable before the visitor authenticates, so
  // they can see what they are joining first. It also carries the organisation's
  // sign-in page brand, which is why the page waits for it before painting.
  useEffect(() => {
    if (!joinToken) return
    let cancelled = false
    axios
      .get(`${process.env.VITE_API_URL}/join/${encodeURIComponent(joinToken)}`)
      .then((response) => {
        if (!cancelled) setJoinContext(response.data)
      })
      .catch(() => {
        // Unreachable is treated the same as invalid: an invalid link still permits
        // ordinary sign-in, so there is nothing to block on.
        if (!cancelled) setJoinContext({ valid: false })
      })
    return () => {
      cancelled = true
    }
  }, [joinToken])

  // Set synchronously, so the already-authenticated effect and the post-sign-in call
  // cannot both fire on the same token. Redemption is idempotent, but each call
  // writes a row to the admin-facing log.
  const redeemStartedRef = useRef(false)

  const redeemAndNavigate = useCallback(async (): Promise<'ok' | 'failed' | 'inflight'> => {
    // 'inflight' means the other caller owns navigation from here.
    if (redeemStartedRef.current) return 'inflight'
    if (!joinToken) return 'failed'
    redeemStartedRef.current = true
    try {
      setRedeeming(true)
      setRedeemError(null)
      const response = await authorizedGraphQLRequest<{
        redeemJoinToken: { success: boolean; message: string | null; grantedCount: number; outcomes: RedeemOutcome[] }
      }>(REDEEM_JOIN_TOKEN, {
        input: { token: joinToken, createdAccount: userStore.createdAccount },
      })

      const result = response.redeemJoinToken
      setRedeemOutcomes(result.outcomes || [])

      if (!result.success) {
        setRedeemError(result.message || t('joinLinks.auth.failed'))
        return 'failed'
      }

      // The organisation may have changed; userStore.user still holds the
      // pre-redemption payload, and /me re-reads the organisation from the database.
      await userStore.checkAuth()
      return 'ok'
    } catch (err: any) {
      const refusal = err?.response?.errors?.[0]
      setRedeemError(refusal?.extensions?.code === JOIN_LINK_PERSONAL_ONLY ? t('joinLinks.auth.personalOnly') : refusal?.message || t('joinLinks.auth.failed'))
      return 'failed'
    } finally {
      setRedeeming(false)
      setJoinToken(null)
    }
  }, [joinToken, userStore, t])

  useEffect(() => {
    const checkAuth = async () => {
      const isAuth = await userStore.checkAuth()
      if (isAuth && !joinToken) {
        navigate(from, { replace: true })
      }
    }

    // A join token suppresses the auto-redirect. App JWTs carry no expiresIn, so a
    // returning user is essentially always already authenticated — left as-is, the
    // common case would be bounced to '/' with the token silently discarded, in
    // precisely the situation the redemption path exists to serve.
    if (!userStore.isLoading && !userStore.isAuthenticated) {
      checkAuth()
    } else if (userStore.isAuthenticated && !joinToken) {
      navigate(from, { replace: true })
    }
  }, [userStore.isAuthenticated, navigate, from, userStore, joinToken])

  // An already-authenticated visitor has no sign-in request to carry the token, so
  // redemption has to run against the existing session.
  useEffect(() => {
    if (!joinToken || !userStore.isAuthenticated || redeeming || redeemOutcomes) return
    // Wait for the link context so an invalid link is reported rather than redeemed.
    if (!joinContext?.valid) return

    let cancelled = false
    redeemAndNavigate().then(async (outcome) => {
      if (cancelled || outcome !== 'ok') return
      await settingsStore.fetchLLMSettings()
      navigate(settingsStore.onboarded ? from : '/welcome', { replace: true })
    })
    return () => {
      cancelled = true
    }
  }, [joinToken, joinContext, userStore.isAuthenticated, redeeming, redeemOutcomes, redeemAndNavigate, settingsStore, navigate, from])

  const finishSignIn = async () => {
    // Redemption runs before navigation so the user lands in the link's module.
    if (joinToken && joinContext?.valid) {
      const outcome = await redeemAndNavigate()
      // 'failed' leaves the outcomes on screen instead of navigating away from them;
      // 'inflight' means the already-authenticated effect is navigating instead.
      if (outcome !== 'ok') return
    }

    await settingsStore.fetchLLMSettings()

    if (!settingsStore.onboarded) {
      navigate('/welcome', { replace: true })
    } else {
      navigate(from, { replace: true })
    }
  }

  const handleGoogleSuccess = async (token: string) => {
    try {
      if (!token) {
        throw new Error('No credential received from Google')
      }

      await userStore.loginWithGoogle(token, joinToken ?? undefined)
      await finishSignIn()
    } catch (error) {
      console.error('Google authentication failed:', error)
    }
  }

  const handleMicrosoftSuccess = async (token: string) => {
    try {
      if (!token) {
        throw new Error('No credential received from Microsoft')
      }

      await userStore.loginWithMicrosoft(token, joinToken ?? undefined)
      await finishSignIn()
    } catch (error) {
      console.error('Microsoft authentication failed:', error)
    }
  }

  const login = useGoogleLogin({
    onSuccess: (codeResponse) => handleGoogleSuccess(codeResponse.access_token),
    onError: (error) => console.log('Login Failed:', error),
  })

  const handleAuthError = (provider: string) => {
    console.error(`${provider} login failed`)
  }

  const [emailCodeStep, setEmailCodeStep] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [emailCodeLoading, setEmailCodeLoading] = useState(false)
  const [emailCodeError, setEmailCodeError] = useState<string | null>(null)
  const [emailCodeSuccess, setEmailCodeSuccess] = useState<string | null>(null)

  const handleSendCode = async () => {
    if (!email) return
    try {
      setEmailCodeLoading(true)
      setEmailCodeError(null)
      userStore.setError(null)
      await axios.post(`${process.env.VITE_API_URL}/auth/email/send-code`, { email: normalizeEmail(email), joinToken })
      setEmailCodeStep('code')
      setEmailCodeSuccess(t('auth.page.codeSent'))
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        setEmailCodeError(error.response.data.error)
      } else {
        setEmailCodeError(t('auth.page.sendFailed'))
      }
    } finally {
      setEmailCodeLoading(false)
    }
  }

  const handleVerifyCode = async () => {
    if (!code) return
    try {
      setEmailCodeLoading(true)
      setEmailCodeError(null)
      setEmailCodeSuccess(null)

      await userStore.loginWithEmailCode(normalizeEmail(email), code, joinToken ?? undefined)
      await finishSignIn()
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        setEmailCodeError(error.response.data.error)
      } else {
        setEmailCodeError(t('auth.page.verifyFailed'))
      }
    } finally {
      setEmailCodeLoading(false)
    }
  }

  const handleBackToEmail = () => {
    setEmailCodeStep('email')
    setCode('')
    setEmailCodeError(null)
    setEmailCodeSuccess(null)
  }

  // With a token the brand is unknown until the join context answers. Nothing is
  // painted meanwhile: the page must not show Georgia and then swap to the customer.
  if (brandState.status === 'loading') {
    return (
      <Layout withHeader={false} noTopPadding>
        <div className="flex min-h-dvh w-full items-center justify-center bg-white" role="status" aria-label={t('auth.page.loading')}>
          <Loader2 className="h-9 w-9 animate-spin text-[#0F4C3A]" />
        </div>
      </Layout>
    )
  }

  return (
    <Layout withHeader={false} noTopPadding>
      <SignInPage
        brand={brandState.brand}
        modules={brandState.modules}
        cookiesLink={<CookiePreferencesLink className="px-1.5 text-[11.5px] text-[#6B7D72] no-underline hover:text-[#0F4C3A] hover:underline" />}
      >
        <div className="flex flex-col gap-2.5 text-left">
          {/* An invalid link still permits ordinary sign-in. */}
          {joinContext && !joinContext.valid && (
            <div className="rounded-xl border border-amber-100 bg-amber-50/80 px-4 py-3">
              <p className="text-sm text-amber-700">{t('joinLinks.auth.invalid')}</p>
            </div>
          )}

          {redeeming && (
            <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
              <p className="flex items-center justify-center gap-2 text-sm text-gray-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('joinLinks.auth.redeeming')}
              </p>
            </div>
          )}

          {/* Per-module outcomes: what was granted, and what was skipped and why. */}
          {redeemOutcomes && redeemOutcomes.some((o) => o.granted) && (
            <div className="rounded-xl border border-green-100 bg-green-50/80 px-4 py-3">
              <p className="text-sm text-green-700">
                {t('joinLinks.auth.granted', {
                  modules: redeemOutcomes
                    .filter((o) => o.granted)
                    .map((o) => o.moduleName)
                    .join(', '),
                })}
              </p>
            </div>
          )}

          {redeemOutcomes && redeemOutcomes.some((o) => !o.granted) && (
            <div className="rounded-xl border border-amber-100 bg-amber-50/80 px-4 py-3">
              <p className="text-sm text-amber-700">
                {t('joinLinks.auth.skipped', {
                  modules: redeemOutcomes
                    .filter((o) => !o.granted)
                    .map((o) => `${o.moduleName}${o.message ? ` (${o.message})` : ''}`)
                    .join(', '),
                })}
              </p>
            </div>
          )}

          {redeemError && (
            <div className="rounded-xl border border-red-100 bg-red-50/80 px-4 py-3">
              <p className="text-sm text-red-600">{redeemError}</p>
            </div>
          )}

          {userStore.error && (
            <div className="rounded-xl border border-red-100 bg-red-50/80 px-4 py-3">
              <p className="text-sm text-red-600">{userStore.errorCode === JOIN_LINK_PERSONAL_ONLY ? t('joinLinks.auth.personalOnly') : userStore.error}</p>
            </div>
          )}

          {userStore.isLoading ? (
            <div className="flex flex-col items-center justify-center gap-4 py-12">
              <Loader2 className="h-9 w-9 animate-spin text-[#0F4C3A]" />
              <span className="text-sm text-[#6B7D72]">{t('auth.page.signingIn')}</span>
            </div>
          ) : (
            <>
              <Button onClick={() => login()} className={ssoButtonClass} leftIcon={<GoogleIcon />}>
                {t('auth.page.google')}
              </Button>

              <div className="[&>button]:h-12 [&>button]:w-full [&>button]:justify-center [&>button]:gap-2.5 [&>button]:rounded-xl [&>button]:border-[1.5px] [&>button]:border-[#E8EDE9] [&>button]:bg-white [&>button]:text-sm [&>button]:font-semibold [&>button]:text-[#0F4C3A] [&>button]:shadow-none [&>button]:transition-all [&>button]:hover:border-[#D6EBDE] [&>button]:hover:bg-[#F2F8F4] [&>button]:active:scale-[0.99]">
                <MicrosoftLoginButton onSuccess={handleMicrosoftSuccess} onError={() => handleAuthError('Microsoft')} disabled={userStore.isLoading} />
              </div>

              <div className="flex items-center gap-3.5 py-1.5 text-xs text-[#6B7D72]">
                <div className="h-px flex-1 bg-[#E8EDE9]" />
                <span>{t('auth.page.or')}</span>
                <div className="h-px flex-1 bg-[#E8EDE9]" />
              </div>

              {emailCodeError && (
                <div className="rounded-xl border border-red-100 bg-red-50/80 px-4 py-3">
                  <p className="text-sm text-red-600">{emailCodeError}</p>
                </div>
              )}

              {emailCodeSuccess && (
                <div className="rounded-xl border border-green-100 bg-green-50/80 px-4 py-3">
                  <p className="text-sm text-green-700">{emailCodeSuccess}</p>
                </div>
              )}

              {emailCodeStep === 'email' ? (
                <div className="flex flex-col gap-2.5">
                  <Input
                    type="email"
                    placeholder={t('auth.page.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendCode()}
                    className={fieldClass}
                  />
                  <Button
                    onClick={handleSendCode}
                    disabled={!email || emailCodeLoading}
                    className={primaryButtonClass}
                    leftIcon={emailCodeLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  >
                    {emailCodeLoading ? t('auth.page.sending') : t('auth.page.sendCode')}
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-2.5">
                  <button onClick={handleBackToEmail} className="flex items-center gap-1 self-start text-sm text-[#6B7D72] transition-colors hover:text-[#0F4C3A]">
                    <ArrowLeft className="h-3.5 w-3.5" />
                    {email}
                  </button>
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder={t('auth.page.codePlaceholder')}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onKeyDown={(e) => e.key === 'Enter' && handleVerifyCode()}
                    maxLength={6}
                    className={`${fieldClass} text-center text-lg tracking-[0.3em]`}
                  />
                  <Button
                    onClick={handleVerifyCode}
                    disabled={code.length !== 6 || emailCodeLoading}
                    className={primaryButtonClass}
                    leftIcon={emailCodeLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : undefined}
                  >
                    {emailCodeLoading ? t('auth.page.verifying') : t('auth.page.verify')}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </SignInPage>
    </Layout>
  )
})

export default Auth
